import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Loader2, AlertCircle, CheckCircle2, Clock, Send, Inbox, Filter, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  sent:             { label: "Versendet",      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: CheckCircle2 },
  sending:          { label: "Wird gesendet",  cls: "bg-blue-500/15 text-blue-300 border-blue-500/30",          icon: Send },
  pending:          { label: "In Queue",       cls: "bg-blue-500/15 text-blue-300 border-blue-500/30",          icon: Clock },
  pending_provider: { label: "Wartet auf Provider", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30",  icon: AlertCircle },
  failed:           { label: "Fehlgeschlagen", cls: "bg-red-500/15 text-red-300 border-red-500/30",             icon: AlertCircle },
};

const CATEGORY_LABEL = {
  contact: "Kontakt",
  buyback: "Ankauf",
  offer: "Angebot",
  order: "Bestellung",
};

const FILTERS = ["all", "sent", "pending_provider", "failed", "pending", "sending"];

export default function AdminEmails() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [status, setStatus] = useState(null);      // provider config info
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const [q, s] = await Promise.all([
        api.get("/admin/emails", { params }),
        api.get("/admin/emails/status"),
      ]);
      setItems(q.data || []);
      setStatus(s.data);
    } catch (e) {
      toast.error("Konnte Mail-Queue nicht laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const counts = useMemo(() => status?.counts || {}, [status]);

  const retry = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/emails/${id}/retry`);
      if (data?.status === "sent") toast.success("Mail versendet");
      else if (data?.status === "pending_provider") toast.info("Provider nicht konfiguriert — Mail bleibt in Warteschlange");
      else toast.error(data?.error || "Retry fehlgeschlagen");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const retryAll = async () => {
    if (!status?.provider_ready) {
      toast.error("Erst EMAIL_PROVIDER=resend + RESEND_API_KEY in Backend setzen und Backend neu starten.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/emails/retry-pending-provider");
      if (data?.ok) toast.success(`${data.sent} versendet · ${data.failed} fehlgeschlagen`);
      else toast.error("Retry-All fehlgeschlagen");
      await load();
    } catch (e) {
      toast.error("Retry-All fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 text-white" data-testid="admin-emails-page">
      {/* Provider banner */}
      {status && (
        <div className={`p-4 rounded-2xl border ${
          status.provider_ready
            ? "bg-emerald-500/10 border-emerald-500/25"
            : "bg-amber-500/10 border-amber-500/30"
        }`} data-testid="email-provider-banner">
          <div className="flex items-start gap-3">
            {status.provider_ready
              ? <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
              : <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />}
            <div className="flex-1">
              <div className="font-semibold">
                Provider: <span className="uppercase">{status.provider || "keiner"}</span>
                <span className={`ml-2 text-xs ${status.provider_ready ? "text-emerald-300" : "text-amber-300"}`}>
                  {status.provider_ready ? "aktiv & bereit" : "nicht konfiguriert"}
                </span>
              </div>
              <p className="text-xs text-zinc-400 mt-0.5">
                Absender: <span className="font-mono">{status.from_email}</span> · Empfänger: <span className="font-mono">{status.contact_receiver}</span>
              </p>
              {!status.provider_ready && (
                <p className="mt-2 text-xs text-amber-200">
                  Setze im Backend die ENV-Variablen <span className="font-mono">EMAIL_PROVIDER=resend</span>, <span className="font-mono">RESEND_API_KEY=re_…</span> und starte das Backend neu. Danach die wartenden Mails über den Button unten nachsenden.
                </p>
              )}
            </div>
            {status.counts?.pending_provider > 0 && (
              <Button
                onClick={retryAll}
                disabled={busy || !status.provider_ready}
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full h-10 whitespace-nowrap shrink-0 disabled:opacity-50"
                data-testid="retry-pending-provider"
              >
                <Send className="w-4 h-4 mr-1.5" />
                Alle {status.counts.pending_provider} nachsenden
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Mail-Queue</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">E-Mail-Nachrichten</h1>
          <p className="text-sm text-zinc-500 mt-1.5">Alle transaktionalen Mails · Retry möglich · Provider-agnostisch.</p>
        </div>
        <Button onClick={load} variant="outline" className="bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white rounded-full" data-testid="email-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Aktualisieren
        </Button>
      </div>

      {/* Filter */}
      <div className="flex gap-2 text-xs flex-wrap">
        {FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 h-9 rounded-full transition-all ${
              filter === s
                ? "bg-[#DC1928] text-white"
                : "bg-[#141417] border border-white/10 text-zinc-400 hover:text-white"
            }`}
            data-testid={`email-filter-${s}`}
          >
            {s === "all" ? "Alle" : STATUS_META[s]?.label || s}
            {counts[s] > 0 && <span className="ml-1.5 opacity-70">·{counts[s]}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex items-center justify-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-20 rounded-2xl border border-dashed border-white/10 bg-[#0F0F11] text-center" data-testid="email-empty">
          <Inbox className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
          <div className="font-display text-base text-white">Keine E-Mails</div>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-6 md:mx-0" data-testid="email-list">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
              <tr className="border-b border-white/8">
                <th className="text-left py-3 px-4 font-semibold">Status</th>
                <th className="text-left py-3 px-4 font-semibold">Kategorie</th>
                <th className="text-left py-3 px-4 font-semibold">Empfänger</th>
                <th className="text-left py-3 px-4 font-semibold">Betreff</th>
                <th className="text-left py-3 px-4 font-semibold">Zeit</th>
                <th className="text-right py-3 px-4 font-semibold">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const st = STATUS_META[m.status] || STATUS_META.pending;
                const StIcon = st.icon;
                return (
                  <tr key={m.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`email-row-${m.id}`}>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${st.cls}`}>
                        <StIcon className="w-3 h-3" /> {st.label}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-zinc-400 text-xs">{CATEGORY_LABEL[m.category] || m.category}</td>
                    <td className="py-3 px-4 text-white text-xs">{m.to}</td>
                    <td className="py-3 px-4 text-zinc-300 text-xs max-w-[280px] truncate" title={m.subject}>{m.subject}</td>
                    <td className="py-3 px-4 text-zinc-500 text-[11px]">{(m.created_at || "").replace("T", " ").slice(0, 16)}</td>
                    <td className="py-3 px-4 text-right">
                      {(m.status === "failed" || m.status === "pending_provider") && (
                        <button
                          onClick={() => retry(m.id)}
                          disabled={busy}
                          className="text-[11px] px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10 disabled:opacity-50"
                          data-testid={`email-retry-${m.id}`}
                        >
                          Nachsenden
                        </button>
                      )}
                      {m.last_error && (
                        <div className="text-[10px] text-red-400 mt-1 max-w-[200px] truncate" title={m.last_error}>
                          {m.last_error}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
